
import { BlogPost } from "@/types/blog";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { FileEdit, Trash2, ExternalLink, AlertCircle } from "lucide-react";

interface BlogPostTableProps {
  posts: BlogPost[];
  onEdit: (slug: string) => void;
  onDelete: (post: BlogPost) => void;
}

export const BlogPostTable = ({ posts, onEdit, onDelete }: BlogPostTableProps) => {
  console.log("BlogPostTable received posts:", posts);
  
  // Inspect the first post's structure for debugging
  if (posts.length > 0) {
    console.log("First post structure:", JSON.stringify(posts[0], null, 2));
  }

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return "No date";
      
      // Try to parse the date
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateString);
        return "Invalid date";
      }
      
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return "Error";
    }
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableCaption>A list of your blog posts.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[250px]">Title</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Author</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8">
                <div className="flex flex-col items-center justify-center text-gray-500">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p>No blog posts found</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            posts.map((post) => (
              <TableRow key={post.slug || post.id || Math.random().toString()}>
                <TableCell className="font-medium">{post.title || "Untitled"}</TableCell>
                <TableCell>{formatDate(post.date)}</TableCell>
                <TableCell>{post.author || "Unknown"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {post.tags && post.tags.length > 0 ? 
                      post.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {tag}
                        </span>
                      )) : 
                      <span className="text-gray-400 text-xs">No tags</span>
                    }
                  </div>
                </TableCell>
                <TableCell>
                  {post.isPublished ? 
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Published</span> : 
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Draft</span>
                  }
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {post.slug && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink size={14} />
                          <span className="sr-only">View</span>
                        </a>
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onEdit(post.slug)}
                      disabled={!post.slug}
                    >
                      <FileEdit size={14} />
                      <span className="sr-only">Edit</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onDelete(post)}
                    >
                      <Trash2 size={14} />
                      <span className="sr-only">Delete</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
